import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { getErrorMessage } from '@/hooks/useMutationWithHandler'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { getEInvoice, getEInvoiceItems } from '../services'
import { EInvoice, EInvoiceItems } from '../types'

// Đang phát hành: hỏi lại mỗi 10 giây, tối đa ~5 phút (quá thì để user bấm Làm mới)
const POLL_INTERVAL_MS = 10_000
const POLL_WINDOW_MS = 5 * 60_000

const NOT_ISSUED = 'e-invoice-not-issued'

export const eInvoiceQueryKey = (invoiceID: number) =>
    [QUERY_KEYS.INVOICE_E_INVOICE, invoiceID] as const

/**
 * Hoá đơn điện tử của hoá đơn DC. `null` = chưa phát hành (`e-invoice-not-issued`).
 * Mỗi lần GET, backend tự hỏi lại MISA trạng thái cấp mã CQT.
 */
export const useEInvoice = (invoiceID: number) =>
    useQueryWithHandler<EInvoice | null>(
        {
            queryKey: eInvoiceQueryKey(invoiceID),
            queryFn: async () => {
                try {
                    return (await getEInvoice(invoiceID))?.result ?? null
                } catch (error) {
                    if (getErrorMessage(error) === NOT_ISSUED) return null
                    throw error
                }
            },
            // App để staleTime mặc định 5 phút: trạng thái phát hành / cấp mã cần lấy mới
            staleTime: 0,
            refetchInterval: (query) => {
                const eInvoice = query.state.data
                if (eInvoice?.status !== 'publishing') return false
                const elapsed =
                    Date.now() - new Date(eInvoice.created_at).getTime()
                return elapsed < POLL_WINDOW_MS ? POLL_INTERVAL_MS : false
            },
        },
        { showLoading: false, showError: false }
    )

/**
 * Các dòng hàng + nội dung sẽ in trên hoá đơn điện tử (nội dung đã lưu khi phát hành, chưa lưu
 * thì hệ thống gợi ý). Cùng tiền tố key với useEInvoice nên phát hành xong sẽ tự lấy lại.
 */
export const useEInvoiceItems = (invoiceID: number) =>
    useQueryWithHandler<EInvoiceItems | null>(
        {
            queryKey: [...eInvoiceQueryKey(invoiceID), 'items'],
            queryFn: async () =>
                (await getEInvoiceItems(invoiceID))?.result ?? null,
            staleTime: 0,
        },
        { showLoading: false, showError: false }
    )

const KEBAB_ERROR_KEY = /^[a-z][a-z0-9-]*$/

/**
 * Mã lỗi backend → câu hiển thị (`invoice:e_invoice_errors.<mã>`, rồi `axios-error`).
 * `misa-<mã>` chưa có bản dịch → "Lỗi từ MISA: <mã> — báo IT". HTTP 403 → không có quyền.
 */
export const useEInvoiceErrorText = () => {
    const { t } = useTranslation('invoice')
    const { t: tAxiosError } = useTranslation('axios-error')

    return useCallback(
        (error: unknown) => {
            const status = (error as { response?: { status?: number } })
                ?.response?.status
            if (status === 403) return t('e_invoice_errors.forbidden')
            const code = getErrorMessage(error)?.trim() ?? ''
            if (KEBAB_ERROR_KEY.test(code)) {
                const translated =
                    t(`e_invoice_errors.${code}`, { defaultValue: '' }) ||
                    tAxiosError(code, { defaultValue: '' })
                if (translated) return translated
                if (code.startsWith('misa-')) {
                    return t('e_invoice_errors.misa-other', {
                        code: code.slice('misa-'.length),
                    })
                }
            }
            return t('e_invoice_errors.unknown')
        },
        [t, tAxiosError]
    )
}
